/**
 * Streaming utilities for incremental bot responses.
 *
 * The webhook-proxy forwards n8n's streaming response, which uses NDJSON
 * (newline-delimited JSON) chunks of the form:
 *
 *   {"type":"begin","metadata":{...}}
 *   {"type":"item","content":"...token...","metadata":{...}}
 *   {"type":"end","metadata":{...}}
 *
 * `Content-Type` is `application/json` (an n8n quirk) and browsers strip
 * `Transfer-Encoding` from the visible headers, so streaming detection by
 * headers alone is unreliable. Instead, we peek the first line of the body:
 * if it parses as a streaming event (`type` ∈ {begin, item, end}), we treat
 * the response as NDJSON; otherwise we drain it and parse as a single JSON.
 */

const STREAM_EVENT_TYPES = new Set(['begin', 'item', 'end']);
const PEEK_BYTE_LIMIT = 4096;

export async function detectStreaming(response) {
  if (!response || !response.body || typeof response.body.getReader !== 'function') {
    return { isStream: false, json: null };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  const initialChunks = [];
  let buffer = '';

  while (!buffer.includes('\n') && buffer.length < PEEK_BYTE_LIMIT) {
    const { value, done } = await reader.read();
    if (done) break;
    initialChunks.push(value);
    buffer += decoder.decode(value, { stream: true });
  }

  const newlineIdx = buffer.indexOf('\n');
  const firstLine = (newlineIdx >= 0 ? buffer.slice(0, newlineIdx) : buffer).trim();

  let firstParsed = null;
  if (firstLine) {
    try {
      firstParsed = JSON.parse(firstLine);
    } catch (_) {
      /* not JSON */
    }
  }

  const looksLikeStream =
    firstParsed &&
    typeof firstParsed === 'object' &&
    !Array.isArray(firstParsed) &&
    typeof firstParsed.type === 'string' &&
    STREAM_EVENT_TYPES.has(firstParsed.type);

  if (!looksLikeStream) {
    let rest = buffer;
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      rest += decoder.decode(value, { stream: true });
    }
    rest += decoder.decode();
    try {
      reader.releaseLock();
    } catch (_) {
      /* noop */
    }

    if (!rest.trim()) return { isStream: false, json: null, text: '' };

    let json = null;
    try {
      json = JSON.parse(rest);
    } catch (_) {
      json = null;
    }
    return { isStream: false, json, text: rest };
  }

  const replayedBody = new ReadableStream({
    async start(controller) {
      for (const chunk of initialChunks) controller.enqueue(chunk);
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          controller.enqueue(value);
        }
        controller.close();
      } catch (e) {
        controller.error(e);
      } finally {
        try {
          reader.releaseLock();
        } catch (_) {
          /* noop */
        }
      }
    }
  });

  return {
    isStream: true,
    response: { body: replayedBody, headers: response.headers }
  };
}

export async function* parseNdjsonStream(response) {
  if (!response || !response.body || typeof response.body.getReader !== 'function') {
    throw new Error('Response body is not a readable stream');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        const trailing = buffer.trim();
        if (trailing) {
          for (const line of buffer.split(/\r?\n/)) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            const parsed = safeParseJson(trimmed);
            if (parsed !== undefined) yield parsed;
          }
        }
        return;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        const parsed = safeParseJson(trimmed);
        if (parsed !== undefined) yield parsed;
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch (_) {
      /* noop */
    }
  }
}

function safeParseJson(text) {
  try {
    return JSON.parse(text);
  } catch (_) {
    return undefined;
  }
}

export async function consumeStream(response, callbacks = {}, options = {}) {
  const { onBegin, onChunk, onEnd, onError } = callbacks;
  const inactivityTimeoutMs = typeof options.inactivityTimeoutMs === 'number' ? options.inactivityTimeoutMs : 0;
  let accumulated = '';
  let started = false;

  // Inactivity watchdog: aborts the underlying body reader if no event arrives
  // within `inactivityTimeoutMs`. We tee an internal AbortController so we can
  // also propagate caller-provided abort via options.signal.
  const watchdog = createInactivityWatchdog(response, inactivityTimeoutMs, options.signal);

  try {
    for await (const event of parseNdjsonStream(response)) {
      watchdog.kick();
      if (!event || typeof event !== 'object') continue;

      if (event.type === 'begin') {
        if (!started) {
          started = true;
          if (onBegin) onBegin(event);
        }
      } else if (event.type === 'item') {
        // Accept both `content` (standard) and `text` (some n8n configurations).
        const chunk =
          (typeof event.content === 'string' ? event.content : '') ||
          (typeof event.text === 'string' ? event.text : '');
        if (chunk) {
          if (!started) {
            started = true;
            if (onBegin) onBegin(event);
          }
          accumulated += chunk;
          if (onChunk) onChunk(chunk, accumulated, event);
        }
      } else if (event.type === 'end') {
        // Per-phase marker, not the actual end of stream.
        // Fallback: some n8n workflows put the full response in the end event
        // (e.g. metadata.output) when no item events are emitted because the
        // underlying model does not support token-by-token streaming.
        if (!accumulated) {
          const fallback =
            (typeof event.output === 'string' ? event.output : '') ||
            (event.metadata && typeof event.metadata.output === 'string' ? event.metadata.output : '');
          if (fallback) {
            if (!started) {
              started = true;
              if (onBegin) onBegin(event);
            }
            accumulated = fallback;
            if (onChunk) onChunk(fallback, accumulated, event);
          }
        }
      } else if (event.type === 'error') {
        const err = new Error(event.message || 'Stream error');
        if (onError) onError(err, event);
        throw err;
      }
    }
  } catch (err) {
    if (watchdog.tripped) {
      const timeoutErr = new Error('Stream inactivity timeout');
      timeoutErr.name = 'TimeoutError';
      if (onError) onError(timeoutErr);
      throw timeoutErr;
    }
    if (onError) onError(err);
    throw err;
  } finally {
    watchdog.dispose();
  }

  if (onEnd) onEnd(accumulated, null);
  return accumulated;
}

function createInactivityWatchdog(response, timeoutMs, externalSignal) {
  const state = { tripped: false };
  if ((!timeoutMs || timeoutMs <= 0) && !externalSignal) {
    return {
      kick() {},
      dispose() {},
      get tripped() {
        return state.tripped;
      }
    };
  }

  let timer = null;
  const cancelBody = () => {
    try {
      if (response && response.body && typeof response.body.cancel === 'function') {
        response.body.cancel();
      }
    } catch (_) {
      /* noop */
    }
  };

  const onTimeout = () => {
    state.tripped = true;
    cancelBody();
  };

  const onExternalAbort = () => {
    cancelBody();
  };

  const arm = () => {
    if (timer) clearTimeout(timer);
    if (timeoutMs > 0) timer = setTimeout(onTimeout, timeoutMs);
  };

  if (externalSignal) {
    if (externalSignal.aborted) onExternalAbort();
    else externalSignal.addEventListener('abort', onExternalAbort, { once: true });
  }

  arm();

  return {
    kick: arm,
    dispose() {
      if (timer) clearTimeout(timer);
      if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
    },
    get tripped() {
      return state.tripped;
    }
  };
}
